import type { HexMapState } from '../render/renderer';
import type { Settlement } from '../map/settlement';
import { canAffordCost } from '../map/settlement';
import { canQueueBuilding } from '../economy/settlementLimits';
import { scaleCost } from '../economy/costScaling';
import type { BuildingType } from '../economy/buildingDefs';
import {
    ACTION__SETTLEMENT__BUILD_FARM,
    ACTION__SETTLEMENT__BUILD_HUNTING_HUT,
    ACTION__SETTLEMENT__BUILD_FISHING_HUT,
    ACTION__SETTLEMENT__BUILD_SMOKEHOUSE,
    ACTION__SETTLEMENT__BUILD_WEAVER,
    ACTION__SETTLEMENT__BUILD_BLACKSMITH,
    ACTION__SETTLEMENT__BUILD_GOLD_MINE,
    ACTION__SETTLEMENT__BUILD_MINT,
    ACTION__SETTLEMENT__BUILD_CARAVAN_POST,
    ACTION__SETTLEMENT__BUILD_STABLE,
    ACTION__SETTLEMENT__BUILD_HOUSE,
    ACTION__SETTLEMENT__BUILD_STONE_HOUSE,
    ACTION__SETTLEMENT__BUILD_LUMBERJACK,
    ACTION__SETTLEMENT__BUILD_SAWMILL,
    ACTION__SETTLEMENT__BUILD_QUARRY,
    ACTION__SETTLEMENT__BUILD_MASONRY,
    ACTION__SETTLEMENT__BUILD_CHARCOAL_PIT,
    ACTION__SETTLEMENT__BUILD_IRON_MINE,
    ACTION__SETTLEMENT__BUILD_SMELTER,
    ACTION__SETTLEMENT__BUILD_BARRACKS,
} from '../input/actionIds';
import { t } from '../../i18n/translations';

type RequirementResult = { ok: boolean; reasonKey?: string; reasonArgs?: Array<string | number> };

type BuildActionMeta = {
    actionId: string;
    buildingType: BuildingType;
    labelKey: string;
    cost: (state: HexMapState, settlement: Settlement) => Record<string, number>;
    requirement?: (state: HexMapState, settlement: Settlement) => RequirementResult;
};

const BUILD_ACTIONS: BuildActionMeta[] = [
    {
        actionId: ACTION__SETTLEMENT__BUILD_HOUSE,
        buildingType: 'house',
        labelKey: 'buildings.label.house',
        cost: (state, settlement) =>
            scaleCost(
                state.config.buildings.woodHouse.cost || {},
                settlement.buildings.houses + 1,
                state.config.buildings.woodHouse.costScaling || { a: 0, b: 0 },
            ),
    },
    {
        actionId: ACTION__SETTLEMENT__BUILD_STONE_HOUSE,
        buildingType: 'stoneHouse',
        labelKey: 'buildings.label.stoneHouse',
        cost: (state, settlement) =>
            scaleCost(
                state.config.buildings.stoneHouse.cost || {},
                settlement.buildings.stoneHouse + 1,
                state.config.buildings.stoneHouse.costScaling || { a: 0, b: 0 },
            ),
    },
    {
        actionId: ACTION__SETTLEMENT__BUILD_LUMBERJACK,
        buildingType: 'lumberjackCamp',
        labelKey: 'buildings.label.lumberjackCamp',
        cost: (state) => state.config.buildings.lumberjackCamp.cost || {},
    },
    {
        actionId: ACTION__SETTLEMENT__BUILD_SAWMILL,
        buildingType: 'sawmill',
        labelKey: 'buildings.label.sawmill',
        cost: (state) => state.config.buildings.sawmill.cost || {},
    },
    {
        actionId: ACTION__SETTLEMENT__BUILD_FARM,
        buildingType: 'farm',
        labelKey: 'buildings.label.farm',
        cost: (state) => state.config.buildings.farm.cost || {},
    },
    {
        actionId: ACTION__SETTLEMENT__BUILD_HUNTING_HUT,
        buildingType: 'huntingHut',
        labelKey: 'buildings.label.huntingHut',
        cost: (state) => state.config.buildings.huntingHut.cost || {},
    },
    {
        actionId: ACTION__SETTLEMENT__BUILD_FISHING_HUT,
        buildingType: 'fishingHut',
        labelKey: 'buildings.label.fishingHut',
        cost: (state) => state.config.buildings.fishingHut.cost || {},
        requirement: (_state, settlement) => ({
            ok: settlement.regionResources.fishCap > 0,
            reasonKey: 'overlay.alertReasonFish',
        }),
    },
    {
        actionId: ACTION__SETTLEMENT__BUILD_SMOKEHOUSE,
        buildingType: 'smokehouse',
        labelKey: 'buildings.label.smokehouse',
        cost: (state) => state.config.buildings.smokehouse.cost || {},
    },
    {
        actionId: ACTION__SETTLEMENT__BUILD_WEAVER,
        buildingType: 'weaver',
        labelKey: 'buildings.label.weaver',
        cost: (state) => state.config.buildings.weaver.cost || {},
    },
    {
        actionId: ACTION__SETTLEMENT__BUILD_BLACKSMITH,
        buildingType: 'blacksmith',
        labelKey: 'buildings.label.blacksmith',
        cost: (state) => state.config.buildings.blacksmith.cost || {},
    },
    {
        actionId: ACTION__SETTLEMENT__BUILD_GOLD_MINE,
        buildingType: 'goldMine',
        labelKey: 'buildings.label.goldMine',
        cost: (state) => state.config.buildings.goldMine.cost || {},
        requirement: (_state, settlement) => ({
            ok: settlement.regionResources.goldDeposit > 0,
            reasonKey: 'overlay.alertReasonGold',
        }),
    },
    {
        actionId: ACTION__SETTLEMENT__BUILD_MINT,
        buildingType: 'mint',
        labelKey: 'buildings.label.mint',
        cost: (state) => state.config.buildings.mint.cost || {},
    },
    {
        actionId: ACTION__SETTLEMENT__BUILD_CARAVAN_POST,
        buildingType: 'caravanPost',
        labelKey: 'buildings.label.caravanPost',
        cost: (state) => state.config.buildings.caravanPost.cost || {},
    },
    {
        actionId: ACTION__SETTLEMENT__BUILD_STABLE,
        buildingType: 'stable',
        labelKey: 'buildings.label.stable',
        cost: (state) => state.config.buildings.stable.cost || {},
    },
    {
        actionId: ACTION__SETTLEMENT__BUILD_QUARRY,
        buildingType: 'quarry',
        labelKey: 'buildings.label.quarry',
        cost: (state) => state.config.buildings.quarry.cost || {},
    },
    {
        actionId: ACTION__SETTLEMENT__BUILD_MASONRY,
        buildingType: 'masonry',
        labelKey: 'buildings.label.masonry',
        cost: (state) => state.config.buildings.masonry.cost || {},
    },
    {
        actionId: ACTION__SETTLEMENT__BUILD_CHARCOAL_PIT,
        buildingType: 'charcoalPit',
        labelKey: 'buildings.label.charcoalPit',
        cost: (state) => state.config.buildings.charcoalPit.cost || {},
    },
    {
        actionId: ACTION__SETTLEMENT__BUILD_IRON_MINE,
        buildingType: 'ironMine',
        labelKey: 'buildings.label.ironMine',
        cost: (state) => state.config.buildings.ironMine.cost || {},
        requirement: (_state, settlement) => ({
            ok: settlement.regionResources.ironDeposit > 0,
            reasonKey: 'overlay.alertReasonIron',
        }),
    },
    {
        actionId: ACTION__SETTLEMENT__BUILD_SMELTER,
        buildingType: 'smelter',
        labelKey: 'buildings.label.smelter',
        cost: (state) => state.config.buildings.smelter.cost || {},
    },
    {
        actionId: ACTION__SETTLEMENT__BUILD_BARRACKS,
        buildingType: 'barracks',
        labelKey: 'buildings.label.barracks',
        cost: (state) => state.config.buildings.barracks.cost || {},
    },
];

function getBuildActionMeta(actionId: string): BuildActionMeta | null {
    return BUILD_ACTIONS.find((entry) => entry.actionId === actionId) || null;
}

function localizeCost(
    lang: HexMapState['config']['ui']['language'],
    cost: Record<string, number>,
): string {
    return Object.entries(cost)
        .map(([key, amount]) => {
            const label = t(lang, `resource.${key}`);
            return `${label === `resource.${key}` ? key : label} ${amount}`;
        })
        .join(', ');
}

export function getBuildActionStatus(
    state: HexMapState,
    settlement: Settlement,
    actionId: string,
): {
    enabled: boolean;
    cost: Record<string, number>;
    labelKey: string;
    reasonKey?: string;
    reasonArgs?: Array<string | number>;
} | null {
    const meta = getBuildActionMeta(actionId);
    if (!meta) return null;
    const cost = meta.cost(state, settlement);
    if (meta.requirement) {
        const requirement = meta.requirement(state, settlement);
        if (!requirement.ok) {
            return {
                enabled: false,
                cost,
                labelKey: meta.labelKey,
                reasonKey: requirement.reasonKey,
                reasonArgs: requirement.reasonArgs,
            };
        }
    }
    if (!canQueueBuilding(settlement, meta.buildingType)) {
        return {
            enabled: false,
            cost,
            labelKey: meta.labelKey,
            reasonKey: 'overlay.alertReasonSlots',
        };
    }
    if (!canAffordCost(settlement, cost)) {
        return {
            enabled: false,
            cost,
            labelKey: meta.labelKey,
            reasonKey: 'overlay.alertReasonResources',
            reasonArgs: [localizeCost(state.config.ui.language, cost)],
        };
    }
    return { enabled: true, cost, labelKey: meta.labelKey };
}
